import { type FunctionComponent } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import { type questionLayout_viewer$data } from './__generated__/questionLayout_viewer.graphql';
import { QuestionTopHeader } from './questionTopHeader/QuestionTopHeader';
import { Answers } from './answers/Answers';
import { RelatedQuestions } from './relatedQuestions/RelatedQuestions';
import { RelatedQuestionItems } from './relatedQuestionItems/RelatedQuestionItems';
import { SellerItems } from './sellerItems/SellerItems';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './questionLayout.scss';

type Props = {
    viewer?: questionLayout_viewer$data;
};

const HEADER_IMAGE_SIZES = '(max-width:1024px) 250px, 325px';

const QuestionLayoutComponent: FunctionComponent<Props> = ({ viewer }) => {
    const question = viewer?.singleQuestion;
    const lastRelatedItem = (question?.relatedQuestionItems?.edges || []).at(-1);
    const headerImage = lastRelatedItem?.node?.item?.photos?.[0];

    if (question) {
        const { title, answerCount } = question;
        if (title) {
            return (
                <div className={styles.container}>
                    <QuestionTopHeader />
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.justifyCenter,
                            dibsCss.myLarge,
                            dibsCss.mxAuto,
                            styles.header
                        )}
                    >
                        <div
                            className={classnames(
                                dibsCss.flex,
                                dibsCss.flexCol,
                                dibsCss.selfCenter,
                                dibsCss.pSmall,
                                styles.headerContent
                            )}
                        >
                            <h1
                                className={classnames(
                                    dibsCss.mx0,
                                    dibsCss.mt0,
                                    dibsCss.mbMedium,
                                    dibsCss.textHeaderBannerSmallNormal,
                                    styles.questionTitle
                                )}
                                data-tn="question"
                            >
                                {title}
                            </h1>
                            {answerCount && answerCount > 0 && (
                                <div
                                    className={classnames(
                                        dibsCss.sassyFontBodyMedium,
                                        dibsCss.textSatan,
                                        dibsCss.mbSmall,
                                        styles.answerCount
                                    )}
                                    data-tn="answer-count"
                                >
                                    <FormattedMessage
                                        id="QuestionsAnswers.answerCount"
                                        defaultMessage="{answerCount, plural, one {# Answer} other {# Answers}}"
                                        values={{ answerCount }}
                                    />
                                </div>
                            )}
                        </div>
                        {headerImage?.masterOrZoomPath && (
                            <div
                                className={classnames(
                                    dibsCss.flex,
                                    dibsCss.justifyCenter,
                                    dibsCss.itemsCenter,
                                    dibsCss.pyMedium,
                                    dibsCss.pxLarger,
                                    styles.headerImageWrapper
                                )}
                            >
                                <img
                                    className={classnames(
                                        dibsCss.relative,
                                        dibsCss.pXlarge,
                                        styles.headerImage
                                    )}
                                    src={headerImage.masterOrZoomPath}
                                    srcSet={getSrcsetString(headerImage.masterOrZoomPath)}
                                    sizes={HEADER_IMAGE_SIZES}
                                    alt={title}
                                />
                            </div>
                        )}
                    </div>
                    <div className={classnames(dibsCss.pbLarge, styles.wrapper)}>
                        <Answers question={question} />
                        <RelatedQuestions question={question} />
                        <RelatedQuestionItems question={question} />
                        <SellerItems question={question} />
                    </div>
                </div>
            );
        }
    }

    return null;
};

export const QuestionLayout = createFragmentContainer(QuestionLayoutComponent, {
    viewer: graphql`
        fragment questionLayout_viewer on Viewer {
            singleQuestion(urlLabel: $urlLabel) {
                title
                url
                answerCount
                relatedQuestionItems {
                    edges {
                        node {
                            item {
                                title
                                photos(limit: 1) {
                                    masterOrZoomPath
                                }
                            }
                        }
                    }
                }
                ...Answers_question
                ...RelatedQuestionItems_question
                ...RelatedQuestions_question
                ...SellerItems_question
            }
        }
    `,
});
